import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import { clearAuthToken, getAuthSessionInfo } from 'modules/auth/api/authService'
import { logoutAzureSession } from 'modules/auth/api/azureAuth'
import {
  AdminMenu,
  CreateUserAccount,
  ContractorManagement,
  AreaMaster,
  CompanyMaster,
  AssignAuthorize,
  SiteConfiguration,
  WorkflowConfiguration,
  DynamicFormConfiguration,
  DynamicReportConfiguration,
  DynamicReportTemplateEditor,
  DynamicReportRunPreview,
  ActiveDirectoryConfiguration,
  AzureAdConfiguration,
  EmailConfiguration,
  EmailTemplateConfiguration,
  PermitDelegationConfiguration,
  SystemConfiguration,
  PlaceholderPage,
  PlotPlanMaster,
  MenuMaster,
  RoleMaster,
  RoleMenuMapping,
  RoleUserMapping,
} from 'modules/admin'
import { PageTitle } from 'shared/components/layout/PageTitle'

type AdminPageKey =
  | 'admin-menu'
  | 'create-user-account'
  | 'contractor-management'
  | 'area-master'
  | 'company-master'
  | 'assign-authorize'
  | 'system-configuration'
  | 'site-configuration'
  | 'workflow-configuration'
  | 'dynamic-form-configuration'
  | 'dynamic-form-configuration-editor'
  | 'dynamic-report-configuration'
  | 'dynamic-report-configuration-editor'
  | 'dynamic-report-run-preview'
  | 'email-configuration'
  | 'email-template-configuration'
  | 'permit-delegation-configuration'
  | 'active-directory-configuration'
  | 'azure-ad-configuration'
  | 'company-management'
  | 'contact'
  | 'plot-plan-master'
  | 'role-master'
  | 'role-menu-mapping'
  | 'role-user-mapping'
  | 'menu-master'

type NavigationState = {
  user?: any
}

const ADMIN_PAGE_KEYS: readonly AdminPageKey[] = [
  'admin-menu',
  'create-user-account',
  'contractor-management',
  'area-master',
  'company-master',
  'assign-authorize',
  'system-configuration',
  'site-configuration',
  'workflow-configuration',
  'dynamic-form-configuration',
  'dynamic-form-configuration-editor',
  'dynamic-report-configuration',
  'dynamic-report-configuration-editor',
  'dynamic-report-run-preview',
  'email-configuration',
  'email-template-configuration',
  'permit-delegation-configuration',
  'active-directory-configuration',
  'azure-ad-configuration',
  'company-management',
  'contact',
  'plot-plan-master',
  'role-master',
  'role-menu-mapping',
  'role-user-mapping',
  'menu-master',
]

const isAdminPageKey = (value: string): value is AdminPageKey =>
  (ADMIN_PAGE_KEYS as readonly string[]).includes(value)

const fallbackAdminUser = {
  name: 'ผู้ดูแลระบบ',
  email: 'admin@example.com',
  role: 'Admin'
}

export function AdminModulePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [currentPage, setCurrentPage] = useState<AdminPageKey>('admin-menu')
  const [selectedDynamicTemplateVersionId, setSelectedDynamicTemplateVersionId] = useState<string>('')
  const [selectedReportTemplateId, setSelectedReportTemplateId] = useState<string>('')
  const [selectedReportTemplateTenantId, setSelectedReportTemplateTenantId] = useState<string>('')

  const navigationState = location.state as NavigationState | null
  const routedUser = navigationState?.user

  // TODO: เชื่อมกับระบบ Auth จริงเมื่อพร้อม
  const currentUser = useMemo(
    () => routedUser ?? fallbackAdminUser,
    [routedUser]
  )

  useEffect(() => {
    if (!routedUser) {
      navigate('/modules', { replace: true })
    }
  }, [routedUser, navigate])

  useEffect(() => {
    if (!routedUser) {
      return
    }

    const wildcardPath = params['*'] ?? ''
    const normalizedPath = wildcardPath.replace(/^\/+|\/+$/g, '')
    if (!normalizedPath) {
      setCurrentPage('admin-menu')
      return
    }

    const nextPage = normalizedPath.split('/')[0]
    setCurrentPage(isAdminPageKey(nextPage) ? nextPage : 'admin-menu')
  }, [params, routedUser])

  if (!routedUser) {
    return null
  }

  const handleNavigate = (page: string) => {
    const nextPage = isAdminPageKey(page) ? page : 'admin-menu'
    setCurrentPage(nextPage)
    navigate(nextPage === 'admin-menu' ? '/admin' : `/admin/${nextPage}`, {
      state: navigationState ?? {},
    })
  }

  const handleLogout = async () => {
    const authSession = getAuthSessionInfo()
    const isContractorAzure =
      authSession?.loginMethod === 'AZURE' &&
      authSession?.userType?.trim().toUpperCase() === 'CONTRACTOR'

    clearAuthToken()

    if (isContractorAzure) {
      try {
        await logoutAzureSession('CONTRACTOR', '/login/contractor')
        return
      } catch (error) {
        console.error('Failed to logout contractor Azure session', error)
      }
    }

    navigate('/login', { replace: true })
  }

  const handleBackToModules = () => {
    navigate('/modules', { replace: true, state: navigationState ?? {} })
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'create-user-account':
        return <CreateUserAccount currentUser={currentUser} />
      case 'contractor-management':
        return <ContractorManagement currentUser={currentUser} />
      case 'area-master':
        return <AreaMaster currentUser={currentUser} />
      case 'company-master':
        return <CompanyMaster currentUser={currentUser} />
      case 'assign-authorize':
        return <AssignAuthorize currentUser={currentUser} />
      case 'system-configuration':
        return <SystemConfiguration />
      case 'site-configuration':
        return <SiteConfiguration currentUser={currentUser} />
      case 'workflow-configuration':
        return <WorkflowConfiguration currentUser={currentUser} />
      case 'dynamic-form-configuration':
        return (
          <DynamicFormConfiguration
            mode="list"
            onConfigure={(templateVersionId: string) => {
              setSelectedDynamicTemplateVersionId(templateVersionId)
              setCurrentPage('dynamic-form-configuration-editor')
            }}
          />
        )
      case 'dynamic-form-configuration-editor':
        return (
          <DynamicFormConfiguration
            mode="editor"
            templateVersionId={selectedDynamicTemplateVersionId}
            onBack={() => setCurrentPage('dynamic-form-configuration')}
          />
        )
      case 'dynamic-report-configuration':
        return (
          <DynamicReportConfiguration
            currentUser={currentUser}
            onConfigure={(reportTemplateId: string, tenantId: string) => {
              setSelectedReportTemplateId(reportTemplateId)
              setSelectedReportTemplateTenantId(tenantId)
              setCurrentPage('dynamic-report-configuration-editor')
            }}
            onRun={(reportTemplateId: string, tenantId: string) => {
              setSelectedReportTemplateId(reportTemplateId)
              setSelectedReportTemplateTenantId(tenantId)
              setCurrentPage('dynamic-report-run-preview')
            }}
          />
        )
      case 'dynamic-report-configuration-editor':
        return (
          <DynamicReportTemplateEditor
            reportTemplateId={selectedReportTemplateId}
            tenantId={selectedReportTemplateTenantId}
            onBack={() => setCurrentPage('dynamic-report-configuration')}
            onRun={(reportTemplateId: string, tenantId: string) => {
              setSelectedReportTemplateId(reportTemplateId)
              setSelectedReportTemplateTenantId(tenantId)
              setCurrentPage('dynamic-report-run-preview')
            }}
          />
        )
      case 'dynamic-report-run-preview':
        return (
          <DynamicReportRunPreview
            reportTemplateId={selectedReportTemplateId}
            tenantId={selectedReportTemplateTenantId}
            onBack={() => setCurrentPage('dynamic-report-configuration')}
          />
        )
      case 'email-configuration':
        return <EmailConfiguration />
      case 'email-template-configuration':
        return <EmailTemplateConfiguration currentUser={currentUser} />
      case 'permit-delegation-configuration':
        return <PermitDelegationConfiguration currentUser={currentUser} />
      case 'active-directory-configuration':
        return <ActiveDirectoryConfiguration />
      case 'azure-ad-configuration':
        return <AzureAdConfiguration />
      case 'company-management':
        return (
          <PlaceholderPage
            title="Company Management"
            description="กำลังพัฒนาหน้าจัดการบริษัท ให้ตรวจสอบกับ System Architect สำหรับข้อมูลเพิ่มเติม"
          />
        )
      case 'plot-plan-master':
        return <PlotPlanMaster currentUser={currentUser} />
      case 'role-master':
        return <RoleMaster currentUser={currentUser} />
      case 'role-menu-mapping':
        return <RoleMenuMapping currentUser={currentUser} />
      case 'role-user-mapping':
        return <RoleUserMapping currentUser={currentUser} />
      case 'menu-master':
        return <MenuMaster currentUser={currentUser} />
      case 'contact':
        return (
          <PlaceholderPage
            title="Contact"
            description="ช่องทางติดต่อฝ่ายสนับสนุนระบบ E-PTW จะถูกรวมไว้ในส่วนนี้"
          />
        )
      case 'admin-menu':
      default:
        return (
          <AdminMenu
            currentUser={currentUser}
            onNavigate={handleNavigate}
          />
        )
    }
  }

  const showPageTitle = currentPage !== 'admin-menu'

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBackToModules}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              ← Back to Modules
            </button>
            <h1 className="text-xl font-semibold text-gray-900">ADMIN Module</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {currentUser.name} ({currentUser.email})
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-800 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {showPageTitle && (
          <PageTitle
            currentPage={currentPage}
            onNavigate={handleNavigate}
          />
        )}
        <div>
          {renderCurrentPage()}
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  )
}
